/**
 * チームの担当の一覧。
 *
 * **在籍者が居ないチームは既定で畳む。** 担当だけが残っている行（チームが無くなったのに
 * 消し忘れたもの）は普段読む必要が無く、出したままにすると現役の行が埋もれる。畳んだことは
 * チェックボックスで分かるようにして、消す判断ができるようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 事業×チームの担当を一覧で出し、在籍者が居ない行を畳んで編集・削除の入口を出す
 * @graph-connects useDeleteTeamAssignment [calls] 担当の削除
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { useDeleteTeamAssignment } from '../../../hooks/useTeamAssignments.js';
import type { TeamAssignment } from '../../../lib/api/team-assignments.js';

/** 表の列の翻訳キー。並びはそのまま列の並びになる。 @graph-connects none */
const COLUMN_KEYS = [
  'teamAssignments.columns.team',
  'teamAssignments.columns.leader',
  'teamAssignments.columns.dle',
  'teamAssignments.columns.dleSub',
  'teamAssignments.columns.dld',
  'teamAssignments.columns.techLead',
  'teamAssignments.columns.members',
] as const;

/** 担当欄の中身。未記入は訳した `-` を出す（空セルと区別する）。 @graph-connects none */
function owners(names: readonly string[], none: string): string {
  return names.length === 0 ? none : names.join(', ');
}

/**
 * 担当の一覧表。
 * @graph-connects none
 */
export function TeamAssignmentTable({
  assignments,
  canManage,
  onEdit,
}: {
  assignments: readonly TeamAssignment[];
  canManage: boolean;
  onEdit: (row: TeamAssignment) => void;
}): ReactNode {
  const { t } = useTranslation();
  const remove = useDeleteTeamAssignment();
  const [showStale, setShowStale] = useState(false);

  const none = t('teamAssignments.none');
  const stale = assignments.filter((row) => row.currentMemberCount === 0);
  const visible = showStale ? assignments : assignments.filter((row) => row.currentMemberCount > 0);

  return (
    <div>
      {stale.length > 0 && (
        <label className="mb-2 flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Checkbox
            checked={showStale}
            onChange={(e) => {
              setShowStale(e.target.checked);
            }}
          />
          {`${t('teamAssignments.showStale')}（${String(stale.length)}）`}
        </label>
      )}

      {visible.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('teamAssignments.empty')}</p>
      ) : (
        <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-hover)]">
                {COLUMN_KEYS.map((key) => (
                  <th
                    key={key}
                    className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap"
                  >
                    {t(key)}
                  </th>
                ))}
                {canManage && (
                  <th className="border-b border-[var(--color-border)] px-3 py-2"> </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visible.map((row, index) => (
                <tr
                  key={`${row.business}/${row.team}`}
                  className={
                    index % 2 === 1
                      ? 'bg-[var(--color-bg-elevated)]'
                      : 'bg-[var(--color-bg-surface)]'
                  }
                >
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    <span className="block">{row.team}</span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      {row.business}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)]">
                    {row.leader === '' ? none : row.leader}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)]">
                    {owners(row.dle, none)}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)]">
                    {owners(row.dleSub, none)}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)]">
                    {owners(row.dld, none)}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)]">
                    {row.techLead === '' ? none : row.techLead}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    {row.currentMemberCount === 0 ? (
                      <span
                        className="text-[var(--color-warning)]"
                        title={t('teamAssignments.noMembersHint')}
                      >
                        {t('teamAssignments.noMembers')}
                      </span>
                    ) : (
                      t('teamAssignments.memberCount', { n: row.currentMemberCount })
                    )}
                  </td>
                  {canManage && (
                    <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            onEdit(row);
                          }}
                        >
                          {t('teamAssignments.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={remove.isPending}
                          onClick={() => {
                            // 消すと「現行チームの一覧」から外れる破壊的操作なので 1 度確かめる。
                            if (
                              !window.confirm(
                                t('teamAssignments.deleteConfirm', {
                                  business: row.business,
                                  team: row.team,
                                }),
                              )
                            ) {
                              return;
                            }
                            remove.mutate({ business: row.business, team: row.team });
                          }}
                        >
                          {t('teamAssignments.delete')}
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {remove.isError && (
        <p className="mt-2 text-sm text-[var(--color-error)]">{remove.error.message}</p>
      )}
    </div>
  );
}
